/**
 * components/tabs/SettingsParts.jsx
 *
 * upstox-073 — the small pieces of the Settings → Account design (the Claude Design handoff,
 * handover/design/settings-account-handoff-2026-10-08.html): a section band (a narrow left
 * column with the title and a description, the content on the right), pills, buttons.
 * Colours are the terminal's tokens (assets/tokens.css) — the handoff's own hex values are
 * the same hues, so no new colour is introduced. Layout only: nothing here sends a request.
 */

import React from 'react';

export const MONO = "'IBM Plex Mono', ui-monospace, monospace";

export const SCOPED_CSS = `
.sx2-root { container-type: inline-size; color: var(--ink); font-family: var(--font-ui); }
.sx2-band { display: flex; flex-direction: column; gap: 16px; padding: 28px 0; }
.sx2-band + .sx2-band { border-top: 1px solid var(--hair); }
.sx2-btn { height: 30px; padding: 0 12px; border: 1px solid var(--hair-strong); background: var(--surface); border-radius: 6px;
  font-size: 13px; font-weight: 500; color: var(--ink); cursor: pointer; font-family: inherit; text-decoration: none;
  display: inline-flex; align-items: center; }
.sx2-btn:hover { background: var(--surface-2); }
.sx2-primary { height: 32px; padding: 0 14px; background: var(--amber-ink); color: #fff; border: none; border-radius: 6px;
  font-size: 13px; font-weight: 600; cursor: pointer; font-family: inherit; }
.sx2-primary:hover:not(:disabled) { filter: brightness(.92); }
.sx2-primary:disabled { background: var(--surface-3); color: var(--ink-3); cursor: default; }
.sx2-link { background: none; border: none; padding: 2px 0; font-size: 12px; font-weight: 600; color: var(--amber-ink); cursor: pointer;
  text-decoration: underline; text-decoration-color: transparent; text-underline-offset: 3px; font-family: inherit; }
.sx2-link:hover { text-decoration-color: var(--amber-ink); }
.sx2-ghost { height: 32px; padding: 0 12px; border: none; background: none; font-size: 13px; color: var(--ink-2); cursor: pointer; font-family: inherit; }
.sx2-input { height: 32px; padding: 0 10px; border: 1px solid var(--hair-strong); border-radius: 6px; background: var(--surface);
  font-size: 13px; outline: none; font-family: inherit; color: var(--ink); min-width: 0; box-sizing: border-box; }
.sx2-input:focus, .sx2-wrap:focus-within { border-color: var(--amber-ink); }
.sx2-wrap { display: flex; align-items: center; height: 30px; border: 1px solid var(--hair-strong); border-radius: 6px; background: var(--surface); overflow: hidden; }
.sx2-scroll { overflow-x: auto; }
.sx2-root a { color: var(--amber-ink); text-underline-offset: 2px; }
`;

/**
 * One section: the title with its subtitle on top, the content under it at the full width of the page
 * (the trader's call on 9 Oct: the handoff's narrow left column cost the tables their width).
 */
export function Band({ id, title, children, desc }) {
  return (
    <section className="sx2-band" data-band={id}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxWidth: 680 }}>
        <div style={{ fontSize: 15, fontWeight: 600 }}>{title}</div>
        {(Array.isArray(desc) ? desc : [desc]).filter(Boolean).map((d, i) => (
          <div key={i} data-subtitle style={{ fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.5, textWrap: 'pretty' }}>{d}</div>
        ))}
      </div>
      <div style={{ minWidth: 0 }}>{children}</div>
    </section>
  );
}

const PILL = {
  ok:    { color: 'var(--up-field-ink)',   background: 'var(--up-field-bg)' },
  warn:  { color: 'var(--amber-ink)',      background: 'var(--amber-bg)' },
  bad:   { color: 'var(--down-field-ink)', background: 'var(--down-field-bg)' },
  quiet: { color: 'var(--ink-2)',          background: 'var(--surface-2)' },
};

/** A status pill; `dot` adds the small status dot of the connection cards. */
export function Pill({ kind = 'quiet', dot, children, title, small }) {
  const c = PILL[kind];
  return (
    <span data-pill={kind} title={title} style={{
      display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 500, whiteSpace: 'nowrap',
      padding: small ? '1px 7px' : '2px 8px', borderRadius: 10, color: c.color, background: c.background,
    }}>
      {dot && <span style={{ width: 6, height: 6, borderRadius: '50%', background: c.color }} />}
      {children}
    </span>
  );
}

export const monoStyle = { fontFamily: MONO, fontSize: 12, color: 'var(--ink-2)' };
